import React, {useEffect, useMemo, useState} from 'react';

import {getAuthenticationConfig} from 'src/openapi/admin/apis/ClientConfigApi';
import {store} from 'src/store';

import {Auth0AuthenticationController} from './auth0-authentication-controller';
import {Auth0AuthenticationEnvironment} from './auth0-authentication-environment';
import {AuthenticationContext} from './authentication-context';

import type {ApiDispatchForToolkit} from 'src/openapi/admin/types';


type Props = {
    children: React.ReactElement,
};

type AuthenticationConfigResponse = {
    config?: Record<string, string>,
};

const toAuthenticationConfigFromApi = (response?: AuthenticationConfigResponse | null) => {
    const config = response?.config;
    if (!config) {
        return {};
    }

    return {
        clientId: config.clientId,
        issuerUri: config.issuerUri,
        audience: config.audience,
        source: config.source,
        callback: config.callback,
    };
};

export const AuthenticationGateway: React.FC<Props> = ({children}) => {
    const [isLogoutRequested, requestLogout] = useState(false);
    const [authConfig, setAuthConfig] = useState(toAuthenticationConfigFromApi(null));
    const [hasAttemptedLoad, setHasAttemptedLoad] = useState(false);

    useEffect(() => {
        let isMounted = true;
        const apiDispatch = store.dispatch as unknown as ApiDispatchForToolkit;

        (async () => {
            try {
                const result = await apiDispatch(getAuthenticationConfig());
                const data = result.payload?.data as unknown as AuthenticationConfigResponse | undefined;
                if (isMounted) {
                    setAuthConfig(toAuthenticationConfigFromApi(data));
                }
            } finally {
                if (isMounted) {
                    setHasAttemptedLoad(true);
                }
            }
        })();

        return () => {
            isMounted = false;
        };
    }, []);

    const value = useMemo(() => ({
        config: authConfig,
        isLogoutRequested,
        requestLogout,
    }), [authConfig, isLogoutRequested]);

    const isConfigured = Boolean(authConfig.clientId) && Boolean(authConfig.issuerUri);

    if (!isConfigured && hasAttemptedLoad) {
        return (
            <main>
                <h1>Authentication Configuration Error</h1>
                <p>
                    Authentication configuration is missing.
                    Ensure the server provides <code>clientId</code> and <code>issuerUri</code>.
                </p>
            </main>
        );
    }

    if (!isConfigured) {
        return null;
    }

    return (
        <AuthenticationContext.Provider value={value}>
            <Auth0AuthenticationEnvironment authConfig={authConfig}>
                <Auth0AuthenticationController>
                    {children}
                </Auth0AuthenticationController>
            </Auth0AuthenticationEnvironment>
        </AuthenticationContext.Provider>
    );
};
